import { FC, useRef } from 'react';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AppPage, AppCard } from '../../components/general';
import CalendarRange, { DefaultRangeSelectType } from '../../components/common/CalendarRange';
import { VoxRoiResult } from '../../models/voxRoi';
import styles from './VoxRoi.module.css';

export interface VoxRoiProps {
  status: 'locked' | 'unlocked';
  hasConfig: boolean;
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  onUnlock: () => void;
  unlockError: string | null;
  isConfigLoading: boolean;
  dateRange: Date[] | undefined;
  onDateRangeChange: (range: Date[] | undefined) => void;
  result: VoxRoiResult | null;
  isUsageLoading: boolean;
  onEditConfig: () => void;
}

const usd = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const usd2 = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const pct = (value: number | null) =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;

const breadcrumbs = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'VoX ROI Savings' },
];

const rangeOptions = [
  DefaultRangeSelectType.CurrentMonth,
  DefaultRangeSelectType.Past30Days,
  DefaultRangeSelectType.Past2Weeks,
  DefaultRangeSelectType.PastWeek,
];

export const VoxRoi: FC<VoxRoiProps> = ({
  status,
  hasConfig,
  passphrase,
  onPassphraseChange,
  onUnlock,
  unlockError,
  isConfigLoading,
  dateRange,
  onDateRangeChange,
  result,
  isUsageLoading,
  onEditConfig,
}) => {
  const unmappedUsage = result?.warnings.unmappedUsage;
  const loggedUnmappedRef = useRef<unknown>(null);
  if (unmappedUsage && unmappedUsage.length > 0 && loggedUnmappedRef.current !== unmappedUsage) {
    loggedUnmappedRef.current = unmappedUsage;
    console.warn(
      `${unmappedUsage.length} usage row(s) could not be mapped to a catalog product:`,
      unmappedUsage,
    );
    console.table(unmappedUsage);
  }

  if (status === 'locked') {
    return (
      <AppPage title="VoX ROI Savings" breadcrumbs={breadcrumbs}>
        <AppCard title={hasConfig ? 'Unlock your ROI report' : 'Set up your ROI configuration'}>
          <div className="flex flex-column gap-3" style={{ maxWidth: 420 }}>
            <p className="text-500 text-sm m-0">
              Your cost configuration is encrypted. Enter your passphrase to {hasConfig ? 'unlock' : 'create'} it.
              Mezzo cannot read your costs and cannot recover a lost passphrase.
            </p>
            <label htmlFor="vox-roi-passphrase" className="font-semibold text-sm">Passphrase</label>
            <div className={styles.unlockFieldGroup}>
              <Password
                inputId="vox-roi-passphrase"
                value={passphrase}
                onChange={(event) => onPassphraseChange(event.target.value)}
                feedback={!hasConfig}
                toggleMask
                onKeyDown={(event) => { if (event.key === 'Enter') onUnlock(); }}
              />
              {unlockError && <Message severity="error" text={unlockError} />}
              <Button label={hasConfig ? 'Unlock' : 'Continue setup'} icon="pi pi-lock-open" onClick={onUnlock} className="w-auto" disabled={isConfigLoading} loading={isConfigLoading} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="VoX ROI Savings"
      breadcrumbs={breadcrumbs}
      headerEnd={
        <div className="flex align-items-center gap-3">
          <CalendarRange rangeSelectOptions={rangeOptions} value={dateRange} onValueChange={onDateRangeChange} />
          <Button label="Edit Costs" icon="pi pi-pencil" onClick={onEditConfig} />
        </div>
      }
    >
      {isUsageLoading && (
        <div className="flex justify-content-center p-6">
          <ProgressSpinner style={{ width: 50, height: 50 }} />
        </div>
      )}

      {!isUsageLoading && result && (
        <>
          {/* Savings band — leads the page */}
          <AppCard title="Your estimated savings (ROI)">
            <div className={styles.roiBand}>
              <div className={`${styles.tile} ${styles.savings}`}>
                <div className={styles.tileValue}>{usd(result.savings.total)}</div>
                <div className={styles.tileLabel}>Total Savings</div>
              </div>
              <div className={`${styles.tile} ${styles.savings}`}>
                <div className={styles.tileValue}>{pct(result.savings.pct)}</div>
                <div className={styles.tileLabel}>% Savings</div>
              </div>
              <div className={`${styles.tile} ${styles.savings}`}>
                <div className={styles.tileValue}>{usd2(result.savings.perLoan)}</div>
                <div className={styles.tileLabel}>Savings per Loan</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileValue}>{result.unitCount.toLocaleString('en-US')}</div>
                <div className={styles.tileLabel}>Units</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileValue}>{usd(result.before.totalSpend)}</div>
                <div className={styles.tileLabel}>Total Spend Before</div>
              </div>
              <div className={styles.tile}>
                <div className={styles.tileValue}>{usd(result.after.totalSpend)}</div>
                <div className={styles.tileLabel}>Total Spend After</div>
              </div>
            </div>
          </AppCard>

          {/* Before / After columns */}
          <div className={`${styles.columns} mt-4`}>
            {/* BEFORE */}
            <AppCard title="Before (Pre-Mezzo)" subtitle={`${result.before.providerCount} ${result.before.providerCount === 1 ? 'provider' : 'providers'}`}>
              <div className="flex flex-column gap-3">
                <div className="flex justify-content-between">
                  <span className="text-500">Average cost per order</span>
                  <span className="font-semibold">{usd2(result.before.avgCostPerOrder)}</span>
                </div>
                <div className="flex justify-content-between">
                  <span className="text-500">Avg orders per loan</span>
                  <span className="font-semibold">{result.before.avgOrdersPerLoan.toFixed(2)}</span>
                </div>
                <div className="flex justify-content-between">
                  <span className="text-500">Avg cost per loan</span>
                  <span className="font-semibold">{usd2(result.before.avgCostPerLoan)}</span>
                </div>
                {result.before.providers.length > 0 ? (
                  <div className="mt-2">
                    <span className="text-500 text-sm font-semibold">Prior Providers</span>
                    <div className="mt-1">
                      {result.before.providers.map((provider) => (
                        <div key={provider.productKey} className={styles.providerRow}>
                          <span className={styles.providerName}>
                            {provider.vendor} &mdash; {provider.productDisplayName}
                          </span>
                          <span className={styles.providerCost}>
                            {provider.costPerOrder !== undefined ? usd2(provider.costPerOrder) : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Message severity="info" text="No prior providers configured" className="w-full" />
                  </div>
                )}
              </div>
            </AppCard>

            {/* AFTER */}
            <AppCard title="After (With Mezzo)" subtitle={`${result.unitCount} ${result.unitCount === 1 ? 'unit' : 'units'}`}>
              <div className="flex flex-column gap-3">
                <div className="flex justify-content-between">
                  <span className="text-500">Total actual spend</span>
                  <span className="font-semibold">{usd(result.after.totalSpend)}</span>
                </div>
                <div className="flex justify-content-between">
                  <span className="text-500">Avg cost per loan</span>
                  <span className="font-semibold">{usd2(result.after.avgCostPerLoan)}</span>
                </div>
                <DataTable value={result.after.lines} size="small" stripedRows>
                  <Column field="vendor" header="Vendor" />
                  <Column field="productDisplayName" header="Product" />
                  <Column field="successCount" header="Orders" />
                  <Column field="unitCost" header="Unit Cost" body={(row) => usd2(row.unitCost)} />
                  <Column field="lineTotal" header="Total" body={(row) => usd(row.lineTotal)} />
                </DataTable>
              </div>
            </AppCard>
          </div>

          {/* Warnings */}
          {result.warnings.unmappedUsage.length > 0 && (
            <Message
              severity="warn"
              className="mt-3 w-full"
              text={`${result.warnings.unmappedUsage.length} usage row(s) could not be mapped to a catalog product.`}
            />
          )}
          {result.warnings.productsMissingCost.length > 0 && (
            <Message
              severity="warn"
              className="mt-3 w-full"
              text={`Missing costs for: ${result.warnings.productsMissingCost.join(', ')}`}
            />
          )}
        </>
      )}

      {!isUsageLoading && !result && (
        <AppCard>
          <p className="text-500 text-center">Select a date range to view your ROI report.</p>
        </AppCard>
      )}
    </AppPage>
  );
};

export default VoxRoi;
